import {
  BasicAlertConfigurations,
  DeviceAlertMetricName,
  MetricUnit,
} from '@/__generated__/graphql';
import { useModalStatusMessage } from '@/hooks';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import { GET_BASIC_ALERT_CONFIGURATION } from '@features/deviceAlertsModal/graphql/queries/getBasicAlertConfiguration';
import { zodResolver } from '@hookform/resolvers/zod';
import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { type DefaultValues, useForm } from 'react-hook-form';
import { type FormValues, getUpdateBasicAlertConfigurationInputSchema } from './updateSchema';
import { useEffect, useMemo } from 'react';
import { basicToAdvanced } from '@features/deviceAlertsModal/utils/basicToAdvanced';
import { UPDATE_ALERT_CONFIGURATION } from '@features/deviceAlertsModal/components/alertConfigurationTab/updateMutation';

const getDefaultValues = (deviceId: string, data: Partial<BasicAlertConfigurations>) => {
  const key = data.lastFlow ? 'lastFlow' : data.lastLevel ? 'lastLevel' : null;
  const activeConfiguration: DefaultValues<FormValues> = {
    deviceId,
    min: key ? data[key]?.min ?? undefined : undefined,
    max: key ? data[key]?.max ?? undefined : undefined,
    overflow: key ? data[key]?.overflow ?? undefined : undefined,
    alertType: key === 'lastLevel' ? DeviceAlertMetricName.Level : DeviceAlertMetricName.Flow,
  };
  return activeConfiguration;
};

export const useUpdateForm = (deviceId: string) => {
  const { _, i18n } = useLingui();
  const { openModalSuccessMessage, openModalErrorMessage } = useModalStatusMessage();
  const {
    data: {
      device: {
        alerts: { basicConfigurations },
      },
    },
  } = useSuspenseQuery(GET_BASIC_ALERT_CONFIGURATION, {
    variables: { deviceId },
  });

  const defaultValues = useMemo(
    () => getDefaultValues(deviceId, basicConfigurations),
    [deviceId, basicConfigurations]
  );

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(getUpdateBasicAlertConfigurationInputSchema(i18n)),
  });

  const [mutate] = useMutation(UPDATE_ALERT_CONFIGURATION, {
    onCompleted({ updateAlertConfiguration: { basicConfigurations } }) {
      openModalSuccessMessage(_(msg`Configuración de alertas actualizada correctamente`));
      methods.reset(methods.getValues());
    },
    onError(error) {
      openModalErrorMessage(
        _(msg`Error al actualizar la configuración de alertas: ${error.message}`),
      );
    },
  });

  useEffect(() => {
    methods.reset(getDefaultValues(deviceId, basicConfigurations));
  }, [deviceId, basicConfigurations, methods]);

  const onSubmit = methods.handleSubmit((data) => {
    console.log(data);
    const unit =
      data.alertType === DeviceAlertMetricName.Flow
        ? MetricUnit.LiterPerSecond
        : MetricUnit.Centimeter;

    const basicInput = {
      ...data,
      unit
    }

    const input = basicToAdvanced(basicInput)
    mutate({ variables: { input } });
  });

  return {
    methods,
    onSubmit,
  };
};
